/**
 * HeroMediaSlider — слайдер медиа для hero-блока главной страницы.
 * Логика:
 *  - 1 изображение → статично, 1 видео → loop
 *  - несколько: изображения 8с, видео до конца, авто-переключение вправо→влево
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HeroMediaItem } from '@/lib/cms-hero';

interface HeroMediaSliderProps {
  items: HeroMediaItem[];
  className?: string;
}

const IMAGE_DURATION = 8000; // 8 seconds for images

export const HeroMediaSlider: React.FC<HeroMediaSliderProps> = ({ items, className = '' }) => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const count = items.length;

  const goNext = useCallback(() => {
    if (count <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % count);
      setIsTransitioning(false);
    }, 600);
  }, [count]);

  // Clear timer helper
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Schedule next slide based on current item type
  const scheduleNext = useCallback(() => {
    clearTimer();
    if (count <= 1) return;
    const item = items[current];
    if (!item) return;
    if (item.type === 'image') {
      timerRef.current = setTimeout(goNext, IMAGE_DURATION);
    }
    // For video: handled by onEnded event
  }, [current, count, items, goNext]);

  useEffect(() => {
    scheduleNext();
    return clearTimer;
  }, [scheduleNext]);

  // When current changes, play the new video if it's a video
  useEffect(() => {
    const item = items[current];
    if (!item) return;
    if (item.type === 'video') {
      const vid = videoRefs.current[current];
      if (vid) {
        vid.currentTime = 0;
        vid.play().catch(() => { });
      }
    }
  }, [current, items]);

  const handleVideoEnded = useCallback((idx: number) => {
    if (count <= 1) {
      // Single video — loop
      const vid = videoRefs.current[idx];
      if (vid) {
        vid.currentTime = 0;
        vid.play().catch(() => { });
      }
      return;
    }
    goNext();
  }, [count, goNext]);

  if (!count) return null;

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {items.map((item, idx) => {
        const isCurrent = idx === current;
        const opacity = isCurrent ? (isTransitioning ? 0 : 1) : 0;

        return (
          <div
            key={item.id}
            className="absolute inset-0 w-full h-full"
            style={{
              opacity,
              transition: 'opacity 0.6s ease-in-out',
              pointerEvents: isCurrent ? 'auto' : 'none',
            }}
            aria-hidden={!isCurrent}
          >
            {item.type === 'image' ? (
              // First slide is the LCP image on the home page — hint the browser
              // to prioritise its download. Subsequent slides stay lazy.
              <img
                src={item.url}
                alt=""
                className="w-full h-full object-cover"
                loading={idx === 0 ? 'eager' : 'lazy'}
                decoding="async"
                {...(idx === 0 ? { fetchPriority: 'high' as const } : {})}
              />
            ) : (
              <video
                ref={(el) => { videoRefs.current[idx] = el; }}
                src={item.url}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload={idx === 0 ? 'auto' : 'none'}
                onEnded={() => handleVideoEnded(idx)}
                loop={count === 1}
              />
            )}
          </div>
        );
      })}

      {/* Slide indicators */}
      {count > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {items.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Слайд ${idx + 1}`}
              onClick={() => {
                clearTimer();
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrent(idx);
                  setIsTransitioning(false);
                }, 300);
              }}
              className={`rounded-full transition-all duration-300 ${idx === current
                  ? 'w-6 h-1.5 bg-white'
                  : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/60'
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
